<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowUp, ChevronDown, ChevronUp, Heart, MessageCircle, Trash2, X } from 'lucide-vue-next'
import type { IResReplayForCommentRecord, IResThreadCommentRecord } from '@/types/apis/comments'
import {
  apiDeleteComment,
  apiDeleteReply,
  apiGetCommentsForThread,
  apiGetReplyForComment,
  apiLikeCommentOrReply,
  apiReplayForComment,
  apiReplayForThread,
  apiUnlikeCommentOrReply,
} from '@/apis/comments'
import type { IReqCursorPage } from '@/types/apis/base'
import DataFeedback from '@/components/data-feedback.vue'

const { t } = useI18n()
const props = defineProps<{
  episodeId: number
  scriptId: number
  hideHeader?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()
type Comment = IResThreadCommentRecord & { children: IResReplayForCommentRecord[] }
const comments = ref<Array<Comment>>([])
const totalComments = ref(0)
const commentPageIndex = ref(1)
const commentPageSize = 7
const commentLoading = ref(false)
const commentHasMore = ref(true)
const commentLoaded = ref(false)
const commentScrollRef = ref<HTMLElement>()
const LOAD_MORE_THRESHOLD = 80

const loadThreadComments = async (reset = false) => {
  if (commentLoading.value) return
  if (!reset && !commentHasMore.value) return

  const nextPageIndex = reset ? 1 : commentPageIndex.value
  commentLoading.value = true
  try {
    const res = await apiGetCommentsForThread({
      EpisodeId: props.episodeId,
      PageSize: commentPageSize,
      PageIndex: nextPageIndex,
    })

    const nextItems = res.Body.Items.map(item => ({
      ...item,
      children: [],
    }))

    if (reset) {
      comments.value = nextItems
    } else {
      const loadedItems = comments.value
      const mergedItems = nextItems.filter(item => !loadedItems.some(comment => comment.Id === item.Id))
      comments.value = [...loadedItems, ...mergedItems]
    }

    totalComments.value = res.Body.TotalCount
    commentPageIndex.value = nextPageIndex + 1
    commentHasMore.value = res.Body.HasNextPage && comments.value.length < totalComments.value
    commentLoaded.value = true

    nextTick(() => {
      const scrollContainer = commentScrollRef.value
      if (!scrollContainer || !commentHasMore.value || commentLoading.value) return

      const nearBottom =
        scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight) <= LOAD_MORE_THRESHOLD
      const notScrollable = scrollContainer.scrollHeight <= scrollContainer.clientHeight + LOAD_MORE_THRESHOLD
      if (nearBottom || notScrollable) {
        loadThreadComments()
      }
    })
  } finally {
    commentLoading.value = false
  }
}
onMounted(() => loadThreadComments(true))

const loadMoreThreadComments = () => {
  loadThreadComments(false)
}

const onCommentScroll = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target || commentLoading.value || !commentHasMore.value) return

  const distanceToBottom = target.scrollHeight - (target.scrollTop + target.clientHeight)
  if (distanceToBottom <= LOAD_MORE_THRESHOLD) {
    loadMoreThreadComments()
  }
}

const content = ref('')

const replay = () => {
  const isEpisodeComment = !replayFor.value
  isEpisodeComment ? addCommentForEpisode() : replayForComment()
}

const addCommentForEpisode = async () => {
  if (!content.value.trim()) return
  const res = await apiReplayForThread({
    ScriptId: props.scriptId,
    EpisodeId: props.episodeId,
    Content: content.value,
  })
  comments.value = [
    {
      ...res.Body,
      children: [],
    },
    ...comments.value,
  ]
  content.value = ''
  totalComments.value += 1
  commentHasMore.value = comments.value.length < totalComments.value
}

// 获取回复
// 添加回复（一级评论和二级评论共用）
const replayFor = ref<Comment | IResReplayForCommentRecord>()
const refReplayInput = ref<HTMLInputElement>()
const triggerReplayFor = async (record: Comment | IResReplayForCommentRecord) => {
  replayFor.value = record
  nextTick(() => {
    refReplayInput.value?.focus()
  })
}

// 评论（二级）
const replayForComment = async () => {
  if (!replayFor.value || !content.value.trim()) return
  const replayMsgInfo = replayFor.value
  // 判断是回复一级评论还是二级评论，如果是二级评论，则RootCommentId为它的RootCommentId，如果是一级评论，则RootCommentId为空
  const isReplayAction = 'children' in replayMsgInfo
  const rootCommentId = isReplayAction ? replayMsgInfo.Id : replayMsgInfo.RootCommentId
  const payload = {
    Content: content.value,
    RootCommentId: rootCommentId,
    ScriptId: props.scriptId,
    EpisodeId: props.episodeId,
    ReplyToUserId: replayMsgInfo.UserId,
    ReplyToReplyId: isReplayAction ? void 0 : replayMsgInfo.Id,
  }
  const res = await apiReplayForComment(payload)

  const rootComment = comments.value.find(item => item.Id === rootCommentId)
  if (rootComment) {
    rootComment.children = [res.Body, ...rootComment.children]
    rootComment.ReplyCount += 1

    const replyState = ensureReplyState(rootComment)
    if (rootComment.children.length >= rootComment.ReplyCount) {
      replyState.hasMore = false
    }

    if (!isCommentExpanded(rootComment.Id)) {
      expandedCommentIds.value = [...expandedCommentIds.value, rootComment.Id]
    }
  }

  replayFor.value = void 0
  content.value = ''
}

const expandedCommentIds = ref<number[]>([])
const animatingLikeKeys = ref<Set<string>>(new Set())
const commentReplyState = ref<
  Record<
    number,
    {
      loading: boolean
      hasMore: boolean
      cursorId?: number
      cursorCreateTime?: string
    }
  >
>({})

const getLikeKey = (type: 'comment' | 'reply', id: number) => `${type}-${id}`
const isLikeAnimating = (type: 'comment' | 'reply', id: number) => animatingLikeKeys.value.has(getLikeKey(type, id))
const triggerLikeAnimation = (type: 'comment' | 'reply', id: number) => {
  const key = getLikeKey(type, id)
  animatingLikeKeys.value.add(key)
  setTimeout(() => {
    animatingLikeKeys.value.delete(key)
  }, 220)
}

const isCommentExpanded = (id: number) => expandedCommentIds.value.includes(id)
const hasReply = (comment: Comment) => comment.ReplyCount > 0
const isReplyLoading = (id: number) => commentReplyState.value[id]?.loading ?? false
const isAllRepliesLoaded = (comment: Comment) => {
  if (!hasReply(comment)) return true
  if (comment.children.length >= comment.ReplyCount) return true
  return commentReplyState.value[comment.Id]?.hasMore === false
}
const shouldShowLoadMore = (comment: Comment) => hasReply(comment) && !isAllRepliesLoaded(comment)
const shouldShowExpandLoaded = (comment: Comment) =>
  hasReply(comment) && isAllRepliesLoaded(comment) && !isCommentExpanded(comment.Id) && comment.children.length > 0
const shouldShowCollapse = (comment: Comment) => hasReply(comment) && isCommentExpanded(comment.Id)
const getExpandButtonText = (comment: Comment) => {
  if (isReplyLoading(comment.Id)) return t('component.comments.loadingReplies')
  if (comment.children.length === 0) return t('component.comments.expandReplies', { count: comment.ReplyCount })
  if (isAllRepliesLoaded(comment)) return t('component.comments.expandRepliesShort')
  return t('component.comments.expandMore')
}

const ensureReplyState = (comment: Comment) => {
  let state = commentReplyState.value[comment.Id]
  if (!state) {
    state = {
      loading: false,
      hasMore: comment.ReplyCount > 0,
      cursorId: void 0,
      cursorCreateTime: void 0,
    }
    commentReplyState.value[comment.Id] = state
  }
  return state
}

const toggleExpandForComment = (id: number) => {
  const isExpanded = isCommentExpanded(id)
  expandedCommentIds.value = isExpanded
    ? expandedCommentIds.value.filter(itemId => itemId !== id)
    : [...expandedCommentIds.value, id]
}

const toggleCommentExpand = async (id: number) => {
  const comment = comments.value.find(item => item.Id === id)
  if (!comment || !hasReply(comment)) return

  if (!isCommentExpanded(id)) {
    expandedCommentIds.value = [...expandedCommentIds.value, id]
  }

  if (isAllRepliesLoaded(comment)) return

  const replyState = ensureReplyState(comment)
  if (replyState.loading) return

  const payload: IReqCursorPage<{ RootCommentId: number }> = {
    RootCommentId: id,
    PageSize: 5,
    CursorId: replyState.cursorId,
    CursorCreateTime: replyState.cursorCreateTime,
  }

  replyState.loading = true
  try {
    const res = await apiGetReplyForComment(payload)
    const loadedItems = comment.children
    const nextItems = res.Body.Items.filter(item => !loadedItems.some(reply => reply.Id === item.Id))
    comment.children = [...loadedItems, ...nextItems]

    replyState.cursorId = res.Body.NextCursorId
    replyState.cursorCreateTime = res.Body.NextCursorCreateTime
    replyState.hasMore = res.Body.HasMore && comment.children.length < comment.ReplyCount
  } finally {
    replyState.loading = false
  }
}

// 删除评论/回复
const removeCommentOrReplay = async (type: 'comment' | 'reply', id: number) => {
  if (type === 'comment') {
    await apiDeleteComment({ CommentId: id })
    comments.value = comments.value.filter(comment => comment.Id !== id)
    delete commentReplyState.value[id]
    expandedCommentIds.value = expandedCommentIds.value.filter(itemId => itemId !== id)
    totalComments.value = Math.max(0, totalComments.value - 1)
    commentHasMore.value = comments.value.length < totalComments.value
  } else {
    await apiDeleteReply({ ReplyId: id })
    comments.value.forEach(comment => {
      const beforeCount = comment.children.length
      comment.children = comment.children.filter(reply => reply.Id !== id)
      if (comment.children.length !== beforeCount) {
        comment.ReplyCount = Math.max(0, comment.ReplyCount - 1)
      }
    })
  }
}

// 点赞/取消点赞评论或回复
const toggleLikeForCommentOrReply = async (type: 'comment' | 'reply', id: number, isLiked: boolean) => {
  const targetType = type === 'comment' ? 1 : 2

  if (isLiked) {
    await apiUnlikeCommentOrReply({ targetType, targetId: id })
  } else {
    await apiLikeCommentOrReply({ targetType, targetId: id })
  }

  // 接口成功后再同步本地点赞状态
  if (type === 'comment') {
    const targetComment = comments.value.find(comment => comment.Id === id)
    if (!targetComment) return
    const likeCount = targetComment.LikeCount ?? 0

    targetComment.HasLiked = !isLiked
    targetComment.LikeCount = isLiked ? Math.max(0, likeCount - 1) : likeCount + 1
    triggerLikeAnimation(type, id)
    return
  }

  comments.value.forEach(comment => {
    const targetReply = comment.children.find(reply => reply.Id === id)
    if (!targetReply) return
    const likeCount = targetReply.LikeCount ?? 0

    targetReply.HasLiked = !isLiked
    targetReply.LikeCount = isLiked ? Math.max(0, likeCount - 1) : likeCount + 1

    triggerLikeAnimation(type, id)
  })
}

const handleClose = () => {
  emit('close')
}
</script>

<template>
  <!-- 右侧信息栏（PC端显示） -->
  <div class="w-full h-full flex flex-col">
    <div v-if="!hideHeader" class="flex items-center justify-between border-b border-white/10 p-4">
      <div class="mt-1 text-base font-semibold text-gray-100">
        {{ t('component.comments.title') }}({{ totalComments }})
      </div>
      <button @click="handleClose" class="text-white/70 transition-colors hover:text-white">
        <X :size="26" />
      </button>
    </div>

    <div ref="commentScrollRef" @scroll.passive="onCommentScroll" class="flex-1 overflow-y-auto px-4 pb-3 pt-2">
      <ul class="mt-1.5 flex flex-col">
        <li
          v-for="(comment, index) in comments"
          :key="comment.Id"
          class="border-white/10 py-2.5"
          :class="{ 'border-t': index !== 0 }"
        >
          <div class="flex gap-2.5">
            <div class="size-10 shrink-0 rounded-full bg-white/10 text-3xl flex items-center justify-center">
              {{ comment.UserNick.charAt(0) }}
            </div>
            <div class="min-w-0 flex-1 gap-y-2 flex flex-col">
              <div class="flex items-center justify-between">
                <p class="truncate text-base font-semibold text-gray-100">
                  {{ comment.UserNick }}
                  <span class="text-xs opacity-60 font-normal" v-if="comment.IsOwner"
                    >({{ t('component.comments.self') }})</span
                  >
                </p>
                <button
                  v-if="comment.IsOwner"
                  class="text-white/50 opacity-60"
                  @click="removeCommentOrReplay('comment', comment.Id)"
                >
                  <Trash2 :size="16" />
                </button>
              </div>
              <p class="mt-0.5 whitespace-pre-line text-base text-justify leading-6 text-gray-100">
                {{ comment.Content }}
              </p>
              <img
                v-for="file in comment.Attachments"
                class="mt-2 h-32 w-24 rounded-lg object-cover"
                :src="file.FileUrl"
                :alt="comment.UserNick"
              />
              <div class="mt-1.5 text-xs text-gray-400">{{ comment.CreateTime }}</div>
              <div class="mt-1 flex items-center gap-4 text-xs text-gray-300">
                <button
                  class="inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                  @click="triggerReplayFor(comment)"
                >
                  <MessageCircle :size="16" />
                  {{ t('component.comments.reply') }}
                </button>
                <button
                  class="inline-flex items-center gap-1 transition-colors"
                  :class="comment.HasLiked ? 'text-rose-500' : 'text-gray-300'"
                  @click="toggleLikeForCommentOrReply('comment', comment.Id, comment.HasLiked)"
                >
                  <Heart
                    :size="18"
                    class="transition-all duration-200"
                    :class="[
                      comment.HasLiked ? 'fill-red-500 stroke-red-500' : 'fill-transparent',
                      isLikeAnimating('comment', comment.Id) ? 'scale-125' : 'scale-100',
                    ]"
                  />
                  {{ comment.LikeCount }}
                </button>
              </div>

              <ul
                v-if="comment.children?.length && isCommentExpanded(comment.Id)"
                class="py-4! flex flex-col gap-4 pl-1"
              >
                <li v-for="child in comment.children" :key="child.Id" class="flex gap-1.5">
                  <div class="size-8 shrink-0 rounded-full bg-white/10 text-3xl flex items-center justify-center">
                    {{ child.UserNick.charAt(0) }}
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-between">
                      <p class="truncate text-base font-semibold text-gray-100">
                        {{ child.UserNick }}
                        <span class="text-xs opacity-60 font-normal" v-if="child.IsOwner"
                          >({{ t('component.comments.self') }})</span
                        >
                        <span v-if="!child.ReplyToReplyId && child.ReplyToUserNick" class="font-normal text-white/60">
                          › {{ child.ReplyToUserNick }}</span
                        >
                      </p>
                      <button
                        v-if="child.IsOwner"
                        class="text-white/60"
                        @click="removeCommentOrReplay('reply', child.Id)"
                      >
                        <Trash2 :size="16" />
                      </button>
                    </div>
                    <p class="mt-0.5 whitespace-pre-line text-sm leading-5 text-gray-100">
                      {{ child.Content }}
                    </p>
                    <div class="mt-0.5 text-xs text-gray-400">{{ child.UpdateTime }}</div>
                    <div class="mt-1 flex items-center gap-4 text-xs text-gray-300">
                      <button
                        class="inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                        @click="triggerReplayFor(child)"
                      >
                        <MessageCircle :size="14" />
                        {{ t('component.comments.reply') }}
                      </button>
                      <button
                        class="inline-flex items-center gap-1 transition-colors"
                        :class="child.HasLiked ? 'text-rose-500' : 'text-gray-300'"
                        @click="toggleLikeForCommentOrReply('reply', child.Id, child.HasLiked)"
                      >
                        <Heart
                          :size="14"
                          class="transition-all duration-200"
                          :class="[
                            child.HasLiked ? 'fill-red-500 stroke-red-500' : 'fill-transparent',
                            isLikeAnimating('reply', child.Id) ? 'scale-125' : 'scale-100',
                          ]"
                        />
                        {{ child.LikeCount }}
                      </button>
                    </div>
                  </div>
                </li>
              </ul>

              <button
                v-if="shouldShowLoadMore(comment) || shouldShowExpandLoaded(comment)"
                @click="toggleCommentExpand(comment.Id)"
                :disabled="isReplyLoading(comment.Id)"
                class="mt-1 inline-flex items-center gap-2 text-sm! text-gray-400 opacity-80"
              >
                {{ getExpandButtonText(comment) }}
                <ChevronDown :size="18" class="transition-transform" />
              </button>
              <button
                v-if="shouldShowCollapse(comment)"
                @click="toggleExpandForComment(comment.Id)"
                class="mt-1 inline-flex items-center gap-2 text-sm! text-gray-400"
              >
                {{ t('component.comments.collapse') }}
                <ChevronUp :size="18" class="transition-transform" />
              </button>
            </div>
          </div>
        </li>
      </ul>
      <DataFeedback
        :loading="commentLoading"
        :empty="commentLoaded && comments.length === 0"
        :finished="commentLoaded && comments.length > 0 && !commentHasMore"
      />
    </div>

    <div class="flex items-center px-4 py-2">
      <div
        class="flex flex-col items-center flex-1 rounded-2xl overflow-hidden bg-white/10 text-left text-xs text-gray-400 focus:outline-none"
      >
        <div v-if="replayFor" class="text-left w-full bg-black/20 p-2 px-3 text-xs flex justify-between">
          <span>{{ t('component.comments.replyTo', { name: replayFor.UserNick }) }}</span>
          <X :size="14" class="ml-1 cursor-pointer" @click="replayFor = void 0" />
        </div>
        <div class="flex items-center w-full pl-4 pr-1 py-1">
          <input
            @keydown.enter="replay"
            ref="refReplayInput"
            class="flex-1 text-base! h-full placeholder:text-base text-white! placeholder:text-white/60"
            type="text"
            v-model="content"
            :placeholder="t('component.comments.placeholderComment')"
          />
          <button @click="replay" class="size-9 rounded-full bg-purple-600 text-white flex items-center justify-center">
            <ArrowUp color="white" :size="20" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
